import React, { createContext, useContext, useState } from "react"
import { usePurchase } from "./purchaseContext"
import PostPurchaseHandler, { FlowType } from "../models/postPurchaseHandler"
import useQueryParams from "../hooks/useQueryParams"
import native from "../native/native"
import { useAppState } from "./appStateContext"
import PromoPricingInfo from "../models/promoPricingInfo"

interface State {
  visibleFlowType: FlowType | null
  promoPricing: PromoPricingInfo | null
}

interface Actions {
  purchasePromo: () => void
}

interface Context {
  state: State
  actions: Actions
}

const PostPurchaseContext = createContext<Context | null>(null)

const PostPurchaseProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryParams = useQueryParams()
  const { state: purchaseState, actions: purchaseActions } = usePurchase()
  const ultimatePlan = useAppState().state.productData!.ultimate
  const [flowType, setFlowType] = useState<FlowType | null>(null)

  const purchaseHandler = new PostPurchaseHandler(
    purchaseState.selectedProduct,
    purchaseState.selectedPlan,
    ultimatePlan
  )

  const anyWindow = window as any
  anyWindow.purchaseMade = () => {
    if (queryParams.postPurchaseFlow) {
      if (purchaseHandler.shouldShowFlow) {
        native.logMixpanelEvent("html_paywall_upsell", {
          type: purchaseHandler.upsellType,
        })
        setFlowType(purchaseHandler.flowType)
        return
      }
    }

    native.postNativeEvent("close")
  }

  let promoPricing: PromoPricingInfo | null = null

  if (flowType != null) {
    promoPricing = purchaseHandler.pricingInfo
  }

  const state: State = {
    visibleFlowType: flowType,
    promoPricing,
  }

  const purchasePromo = () => {
    if (purchaseHandler.promoProduct != null) {
      native.subscribeWithDiscount(purchaseHandler.promoProduct)
    }
  }

  const actions: Actions = { purchasePromo }

  return (
    <PostPurchaseContext.Provider value={{ state, actions }}>
      {children}
    </PostPurchaseContext.Provider>
  )
}

const usePostPurchase = () => {
  const context = useContext(PostPurchaseContext)

  if (!context) {
    throw new Error(
      "usePostPurchase has to be used within <PostPurchaseContext.Provider>"
    )
  }

  return context
}

export { PostPurchaseProvider, PostPurchaseContext, usePostPurchase }
